import { useCallback } from 'react';
import { isEmpty } from 'lodash';
import { Typography, Stack } from '@mui/material';
import { useDispatch, useListeners, useSelector } from '@client/hooks/redux.hooks';
import { ImageUpload } from '@client/components/forms/ImageUpload';
import { getSelectedProfile } from '@client/selectors/helpers/profile';
import { ProfileTypes } from '@sda/enums';
import { getImageArrayFromField } from '@sda/utils/image/getImageArrayFromField';
import { SignupWrapperForm } from '../../common/SignupWrapper/SignupWrapperForm';
import { signupArtistPhotoListeneners, startSaveArtistPhoto } from './signup-artist-photo.listeners';
import { SignupArtistPhotoRequiredSchema, SignupArtistPhotoSchema } from './signup-artist-photo.schema';
import type { SignupStepProps } from '../../common/interfaces';

const Explainer = (
  <Stack spacing={2}>
    <Typography
      variant="h6"
      align="left"
    >
      Upload a profile image
    </Typography>

    <Typography
      color="textSecondary"
      align="left"
    >
      Waiting for your new photos? Don't worry, you can always edit or upload more later.
    </Typography>
  </Stack>
);

export const Photo: React.FC<SignupStepProps> = ({ skippable = true }) => {
  const dispatch = useDispatch();
  const selectedProfile = useSelector(getSelectedProfile);

  useListeners(signupArtistPhotoListeneners);

  const initialValues = {
    publicImageUrl: selectedProfile?.profileType === ProfileTypes.ACT ? getImageArrayFromField(selectedProfile.publicImageUrl) : [],
  };

  const handleSubmit = useCallback((values: typeof initialValues) => {
    dispatch(startSaveArtistPhoto(values));
  }, [dispatch]);

  return (
    <SignupWrapperForm
      steps={6}
      currentStep={3}
      renderExplainer={Explainer}
      FormikProps={{
        initialValues,
        onSubmit: handleSubmit,
        validationSchema: skippable ? SignupArtistPhotoSchema : SignupArtistPhotoRequiredSchema,
      }}
      buttonProps={({ values }) => ({
        scope: 'signup-act-photo-form',
        ButtonProps: {
          disabled: !skippable && isEmpty(values.publicImageUrl),
        },
      })}
    >
      <ImageUpload
        name="publicImageUrl"
        multiple={false}
        maxFiles={1}
        required={!skippable}
      />
    </SignupWrapperForm>
  );
};
